import React, { useState, useMemo } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  useMediaQuery,
  LinearProgress,
} from '@mui/material';
import {
  Description as PdfIcon,
  GridOn as ExcelIcon,
  Refresh as RefreshIcon,
  
  People as PeopleIcon,
} from '@mui/icons-material';
import Sidebar from '../components/sidebars/Sidebar';
import TopNav from '../components/top_bars/TopNav';
import StatisticsCards from '../components/top_bars/StatisticsCard';

const ReportExport = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
 
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Calculate total salary and staff count
  const { totalSalary, staffCount } = useMemo(() => {
    const staffCount = data.length;
    const totalSalary = data.reduce((sum, emp) => {
      const salary = parseFloat(emp.salaryInfo?.salary_amount) || 0;
      return sum + salary;
    }, 0);
    return { totalSalary, staffCount };
  }, [data]);

  // Sort data by salary in ascending order
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const salaryA = parseFloat(a.salaryInfo?.salary_amount) || 0;
      const salaryB = parseFloat(b.salaryInfo?.salary_amount) || 0;
      return salaryA - salaryB;
    });
  }, [data]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/report.php`);
      if (res.data.success) {
        setData(res.data.data);
      } else {
        setError(res.data.message || 'No data found');
        setData([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching data');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("All Employees Report", 105, 15, { align: 'center' });

    // Add summary information
    doc.setFontSize(12);
    doc.text(`Total Staff: ${staffCount}`, 14, 25);
    doc.text(`Total Salary Payable: ₦${totalSalary.toLocaleString('en-US')}`, 14, 35);

    let currentY = 45;

    sortedData.forEach((emp, index) => {
      doc.setFontSize(12);
      doc.text(`Employee ${index + 1}`, 14, currentY);
      currentY += 5;

      autoTable(doc, {
        startY: currentY,
        head: [['Full Name', 'Department', 'Phone', 'Email', 'Salary']],
        body: [[
          emp.personalInfo?.full_name || 'N/A',
          emp.personalInfo?.department || 'N/A',
          emp.personalInfo?.phone || 'N/A',
          emp.personalInfo?.email || 'N/A',
          emp.salaryInfo?.salary_amount ? `₦${emp.salaryInfo.salary_amount}` : 'N/A'
        ]],
        theme: 'grid',
        headStyles: { fillColor: [63, 81, 181] }
      });
      currentY = doc.lastAutoTable.finalY + 10;

      autoTable(doc, {
        startY: currentY,
        head: [['Experience Type', 'Years', 'Last Workplace']],
        body: [[
          emp.jobExperience?.experience_type || 'N/A',
          emp.jobExperience?.years_of_experience || 'N/A',
          emp.jobExperience?.last_workplace_one || 'N/A'
        ]],
        theme: 'grid',
        headStyles: { fillColor: [63, 81, 181] }
      });
      currentY = doc.lastAutoTable.finalY + 10;

      autoTable(doc, {
        startY: currentY,
        head: [['Account Number', 'Account Name', 'Bank']],
        body: [[
          emp.salaryInfo?.account_number || 'N/A',
          emp.salaryInfo?.account_name || 'N/A',
          emp.salaryInfo?.bank_name || 'N/A'
        ]],
        theme: 'grid',
        headStyles: { fillColor: [63, 81, 181] }
      });

      currentY = doc.lastAutoTable.finalY + 15;

      // New page if near bottom
      if (currentY > 270) {
        doc.addPage();
        currentY = 25;
      }
    });

    doc.save(`all_employees_report.pdf`);
  };

  const exportExcel = () => {
    const excelData = sortedData.map((emp) => ({
      "Full Name": emp.personalInfo?.full_name || 'N/A',
      "Department": emp.personalInfo?.department || 'N/A',
      "Phone": emp.personalInfo?.phone || 'N/A',
      "Email": emp.personalInfo?.email || 'N/A',
      "Experience Type": emp.jobExperience?.experience_type || 'N/A',
      "Years of Experience": emp.jobExperience?.years_of_experience || 'N/A',
      "Last Workplace": emp.jobExperience?.last_workplace_one || 'N/A',
      "Account Number": emp.salaryInfo?.account_number || 'N/A',
      "Account Name": emp.salaryInfo?.account_name || 'N/A',
      "Bank Name": emp.salaryInfo?.bank_name || 'N/A',
      "Salary Amount": emp.salaryInfo?.salary_amount ? `₦${emp.salaryInfo.salary_amount}` : 'N/A'
    }));

    // Add summary row
    excelData.push({
      "Full Name": "TOTAL",
      "Salary Amount": `₦${totalSalary.toLocaleString('en-US')}`,
      "Department": `Staff Count: ${staffCount}`
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Reports");
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(dataBlob, `all_employees_report.xlsx`);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          maxWidth: '1400px',
          mx: 'auto',
          width: '100%',
        }}
      >
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
          }}
          onNotificationClick={() => alert('You have new notifications!')}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ mt: 2 }}>
          <StatisticsCards />
        </Box>

        <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
          <Typography variant="h4" gutterBottom color="primary">
            All Employee Reports
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchData}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Fetch Staff'}
            </Button>

            {data.length > 0 && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  onClick={exportPDF}
                  startIcon={<PdfIcon />}
                >
                  Export to PDF
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={exportExcel}
                  startIcon={<ExcelIcon />}
                >
                  Export to Excel
                </Button>
              </>
            )}
          </Box>

          {data.length > 0 && (
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Chip
                icon={<PeopleIcon />}
                label={`Total Staff: ${staffCount}`}
                color="primary"
                variant="outlined"
              />
              <Chip
               
                label={`Total Salary Payable: ₦${totalSalary.toLocaleString('en-US')}`}
                color="success"
                variant="outlined"
              />
            </Stack>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {loading && (
            <Box sx={{ mt: 3 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Loading employee data...
              </Typography>
            </Box>
          )}

          {data.length > 0 && (
            <Box sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {sortedData.map((emp, idx) => (
                <Paper key={idx} sx={{ p: 3, mb: 2 }}>
                  <Typography variant="h6" sx={{ color: 'primary.main' }}>
                    {emp.personalInfo?.full_name || `Employee ${idx + 1}`}
                    <Chip 
                      label={`₦${emp.salaryInfo?.salary_amount?.toLocaleString('en-US') || '0'}`} 
                      color="success" 
                      size="small" 
                      sx={{ ml: 2 }} 
                    />
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography><strong>Phone:</strong> {emp.personalInfo?.phone}</Typography>
                      <Typography><strong>Email:</strong> {emp.personalInfo?.email}</Typography>
                      <Typography><strong>Department:</strong> {emp.personalInfo?.department}</Typography>
                      <Typography><strong>DOB:</strong> {emp.personalInfo?.date_of_birth}</Typography>
                      <Typography><strong>Status:</strong> {emp.personalInfo?.marital_status}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography><strong>Experience Type:</strong> {emp.jobExperience?.experience_type || 'N/A'}</Typography>
                      <Typography><strong>Years:</strong> {emp.jobExperience?.years_of_experience || 'N/A'}</Typography>
                      <Typography><strong>Last Workplace:</strong> {emp.jobExperience?.last_workplace_one || 'N/A'}</Typography>
                      <Typography><strong>Account No:</strong> {emp.salaryInfo?.account_number || 'N/A'}</Typography>
                      <Typography><strong>Bank:</strong> {emp.salaryInfo?.bank_name || 'N/A'}</Typography>
                      <Typography><strong>Salary:</strong> ₦{emp.salaryInfo?.salary_amount?.toLocaleString('en-US') || '0.00'}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ReportExport;